<script setup>
import { ref, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { Chart, registerables } from 'chart.js';
import axios from 'axios';
import { parseISO } from 'date-fns';

// Register Chart.js components
Chart.register(...registerables);

// Refs and store
const barChart = ref(null);
const store = useStore();

const startDate = ref(store.getters['dates/start_date']);
const endDate = ref(store.getters['dates/end_date']);
const originalData = ref([]); // Data from API
const requestsDateRange = ref({ start: '', end: '' });
let chartInstance = null;

// Fetch data from API
const fetchData = async () => {
  try {
    const response = await axios.get('requests_by_date/');
    originalData.value = response.data.data; // Store original data
    requestsDateRange.value = {
      start: response.data.start_date,
      end: response.data.end_date,
    };
    return response.data.data;
  } catch (error) {
    console.error('Error fetching data:', error);
    return [];
  }
};

// Destroy the existing chart instance
const destroyChart = () => {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
};

// Create or update the bar chart
const createChart = (data) => {
  const dates = data.map((item) => item.date);
  const counts = data.map((item) => item.count);

  destroyChart(); // Destroy previous chart instance

  chartInstance = new Chart(barChart.value, {
    type: 'line',
    data: {
      labels: dates,
      datasets: [
        {
          label: 'Кол-во заявок',
          data: counts,
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          grid: { display: true },
        },
      },
    },
  });
};

// Filter data based on selected dates
const filterData = () => {
  const filteredData = originalData.value.filter((item) => {
    const date = parseISO(item.date);
    const start = startDate.value ? parseISO(startDate.value) : null;
    const end = endDate.value ? parseISO(endDate.value) : null;
    return (!start || date >= start) && (!end || date <= end);
  });

  createChart(filteredData); // Re-create chart with filtered data
};

// Save selected dates to Vuex and filter data
const saveDates = () => {
  store.dispatch('dates/setStartDate', startDate.value);
  store.dispatch('dates/setEndDate', endDate.value);
  filterData();
};

// Reset the date range to the original bounds
const resetData = () => {
  startDate.value = requestsDateRange.value.start;
  endDate.value = requestsDateRange.value.end;
  saveDates();
};

// Fetch data and create chart on component mount
onMounted(async () => {
  const data = await fetchData();
  if (startDate.value || endDate.value) {
    filterData();
  } else {
    createChart(data);
  }
});

// Watch for changes in startDate and endDate
watch([startDate, endDate], filterData);
</script>

<template>
  <div>
    <!-- Date Range Selector -->
    <div class="row g-2 align-items-end mb-3">
      <div class="col-md-4">
        <label for="start-date" class="form-label mb-0 fw-bold">От:</label>
        <input
            id="start-date"
            type="date"
            v-model="startDate"
            @change="saveDates"
            class="form-control form-control-sm"
        />
      </div>
      <div class="col-md-4">
        <label for="end-date" class="form-label mb-0 fw-bold">До:</label>
        <input
            id="end-date"
            type="date"
            v-model="endDate"
            @change="saveDates"
            class="form-control form-control-sm"
        />
      </div>
      <div class="col-md-4 d-flex justify-content-end">
        <button class="btn btn-sm btn-outline-primary d-flex align-items-center gap-1" @click="resetData">
          <i class="fas fa-rotate-right"></i> Сбросить
        </button>
      </div>
    </div>

    <!-- Bar Chart Canvas -->
    <div class="chart-wrapper">
      <canvas ref="barChart" class="chart-canvas"></canvas>
    </div>
  </div>
</template>

<style scoped>
.chart-wrapper {
  width: 100%;
  height: 400px;
  position: relative;
}

.chart-canvas {
  width: 100%;
  height: 100%;
}
</style>